// Pure predicate for "this issue asks the steward to pick WHICH of
// several real households a mortgage-import stub belongs to."
//
// Cambria 2026-07-31, on a "Which real household is Malcolm Fleming —
// 2 households carry this exact name?" card: "ok so i figured out which
// household is the right one but im not seeing a spot where i can fix
// it." She was right — there was no spot. The detector
// (mortgage-stub-reattach-review) deliberately emits NO recommendation,
// because with 2+ identically-named twins there is no safe default
// survivor to recommend; but "no recommendation" also meant the decide
// rail fell through to its generic disposition buttons (keep separate /
// snooze / assign) and offered no way to record the ANSWER to the
// question it had just asked. The one thing she had figured out was the
// one thing she couldn't tell the system.
//
// The existing 2-household merge affordance (household-cluster-predicate)
// can't cover this: it requires EXACTLY two household refs and a merge
// recommendation, and this issue has three-plus refs and no
// recommendation. So this predicate names the third shape — one stub
// plus N candidate families — and the rail renders a row per candidate,
// each opening the SAME per-field HouseholdMergeReview with that family
// pre-selected as the survivor.
//
// Deliberately an explicit slug allowlist, not a shape heuristic (same
// convention as household-search-merge-predicate.ts): a household issue
// carrying 3+ refs by accident must not sprout a merge picker.
//
// Split out as a pure module so it's unit-testable from tests/web/
// without dragging web/src/api.ts (DOM-typed fetch / import.meta.env)
// into the root tsconfig program.

export type RecordRef = {
  kind: string;
  id: string;
  label: string;
  /** "Tom & Carolyn Fleming" — attached server-side. The whole point of
   *  the picker: two rows both reading "Malcolm Fleming" are impossible
   *  to choose between until you can see who else lives there. */
  members_label?: string | null;
  /** True when this household has since been archived or merged away.
   *  Attached server-side at list time — the detector's frozen record
   *  list can't know what happened after it ran. Absent means "not
   *  provably gone", never "confirmed live". */
  record_gone?: boolean;
};

// Minimal subset of CleanupIssueSummary this predicate inspects.
export interface IssueLike {
  entityKind: string;
  detectorSlug?: string;
  affectedRecords?: RecordRef[];
}

export interface StubTwinChoices {
  /** The mortgage-import placeholder. Always the record being absorbed
   *  — never a merge survivor. */
  stub: RecordRef;
  /** The real households to choose between. */
  candidates: RecordRef[];
  /** True when the placeholder itself has already been archived or
   *  merged away — the question is answered and there is nothing left to
   *  merge. Baker 2026-07-31: the rail offered "This is the family…"
   *  anyway, and the button failed against a record that no longer
   *  existed. The card should not normally reach a user in this state
   *  (both cleanup guards now close it at merge time), so this covers
   *  the tab that was already open, or a direct link to the issue. */
  stubGone: boolean;
}

export const STUB_TWIN_DETECTOR_SLUG = "mortgage-stub-reattach-review";

export function stubTwinChoices(issue: IssueLike): StubTwinChoices | null {
  if (issue.entityKind !== "household") return null;
  if (issue.detectorSlug !== STUB_TWIN_DETECTOR_SLUG) return null;
  const recs = (issue.affectedRecords ?? []).filter(
    (r) => r.kind === "household",
  );
  // The detector's emit order is load-bearing and documented at its
  // recommendation site: [stub, ...candidates]. Reading position rather
  // than parsing the "— mortgage-import stub" label text is deliberate
  // (labels are display copy and have already been reworded once).
  const [stub, ...rest] = recs;
  if (!stub) return null;
  // Never offer the stub as its own destination: a merge of a household
  // into itself is the one outcome that could destroy the records this
  // whole issue exists to rescue.
  const candidates = rest.filter((c) => c.id !== stub.id);
  // The detector only emits at 2+ twins (a single twin is the reattach
  // pass's auto-merge, never a human task). Fewer than 2 here means the
  // issue isn't the shape this picker is written for — fall through to
  // the rail's generic handling rather than guessing.
  if (candidates.length < 2) return null;
  // Gone candidates are kept in the list and disabled by the rail, never
  // filtered out here. If this flag were ever wrong, hiding would silently
  // remove the right answer from a picker whose whole job is to offer it;
  // disabling degrades to a visible, explained row instead.
  return { stub, candidates, stubGone: stub.record_gone === true };
}
